import { getToken } from './auth';
import type {
  ChatActionConfirmResponse,
  ChatConversationCreated,
  ChatConversationSummary,
  PersistedChatMessage,
} from './chatTypes';

import { API_BASE } from './apiBase';

function buildUrl(path: string): string {
  return path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`
}

// Exception enrichie avec le `status` HTTP pour permettre aux appelants de
// détecter un 401 de manière fiable, sans dépendre du contenu du message
// (qui est `{error: "Non authentifié"}` en JSON — sans prefix parseable).
export class ApiError extends Error {
  readonly status: number
  /** Story 8.5 — champ `detail` du corps JSON (ex. 422 : `{ fieldErrors, formErrors }` de zod). */
  readonly detail?: unknown
  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

/** Story 8.5 — premier message d'erreur zod (`detail.fieldErrors[field][0]`) d'une réponse 422, sinon null. */
export function apiFieldError(err: ApiError, field: string): string | null {
  const detail = err.detail
  if (typeof detail !== 'object' || detail === null || !('fieldErrors' in detail)) return null
  const fieldErrors = (detail as { fieldErrors?: Record<string, unknown> }).fieldErrors
  const messages = fieldErrors?.[field]
  return Array.isArray(messages) && typeof messages[0] === 'string' ? messages[0] : null
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError
}

async function buildApiError(res: Response): Promise<ApiError> {
  const text = await res.text()
  try {
    const json = JSON.parse(text)
    if (typeof json.error === 'string') return new ApiError(res.status, json.error, json.detail)
  } catch { /* not JSON — keep raw message */ }
  return new ApiError(res.status, `API error ${res.status}: ${text}`)
}

// Helper interne (non exporté) — réponse brute avec auth pour Blob downloads (CSV, Excel)
// Nécessaire car apiFetch consomme la réponse avant qu'on puisse lire les headers ET le blob
async function apiFetchRaw(path: string): Promise<Response> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(buildUrl(path), { headers })
  if (!res.ok) throw await buildApiError(res)
  return res
}

export async function apiFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(buildUrl(path), {
    ...init,
    headers,
  });
  if (!res.ok) throw await buildApiError(res);
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  // @ts-ignore allow non-json
  return res.text();
}

export async function getHealth() { return apiFetch('/health'); }

// Auth endpoints
export async function apiLogin(email: string, password: string) {
  return apiFetch<{ token: string; user: any }>(`/auth/login`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function apiRegisterOrg(orgName: string, email: string, password: string, name?: string) {
  return apiFetch<{ token: string; user: any; organization: any }>(`/auth/register`, {
    method: 'POST',
    body: JSON.stringify({ orgName, email, password, name }),
  });
}

export async function apiMe() { return apiFetch(`/auth/me`); }

// Users (admin)
export async function apiListUsers() { return apiFetch(`/users`); }
export async function apiInviteUser(payload: { email: string; role: 'ADMIN' | 'MEMBER' }) {
  return apiFetch<{ message: string }>(`/users`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function apiUpdateUser(
  id: string,
  payload: { role?: 'ADMIN' | 'MEMBER'; isActive?: boolean }
) {
  return apiFetch<{ user: { id: string; email: string; name?: string | null; role: 'ADMIN' | 'MEMBER'; isActive: boolean; createdAt: string } }>(
    `/users/${id}`,
    { method: 'PATCH', body: JSON.stringify(payload) }
  )
}

// Story 8.5 — droit à l'oubli : suppression définitive d'un compte
export async function apiDeleteMyAccount(password: string) {
  return apiFetch<{ message: string }>(`/users/me`, { method: 'DELETE', body: JSON.stringify({ password }) })
}

export async function apiDeleteUser(id: string) {
  return apiFetch<{ message: string }>(`/users/${id}`, { method: 'DELETE' })
}

// Invitation acceptance (public — no Bearer token needed)
export async function apiGetInvite(token: string): Promise<{ email: string; orgName: string }> {
  return apiFetch<{ email: string; orgName: string }>(`/auth/invite/${token}`);
}
export async function apiActivateInvite(
  token: string,
  payload: { name?: string; password: string }
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/auth/invite/${token}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Story 4.3 — redemander un lien d'invitation lorsqu'il est expiré ou
// considéré invalide. Route publique (pas de Bearer requis). Le backend
// renvoie toujours 200 + message générique (anti-énumération d'emails).
export async function apiResendInvite(email: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/auth/invite/resend`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

// Templates
export type FormTemplate = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  category?: string | null;
  sections: unknown; // JSON — typé TemplateSection[] dans les composants qui l'utilisent
  isSystem: boolean;
  aiGenerated?: boolean; // Story 6.2 — template généré par l'agent IA
  status?: string | null; // Story 6.4 — 'draft' (brouillon IA) | 'active' | null (ancien)
  organizationId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type TemplateField = {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'file' | 'date';
  unit?: string;
  description?: string;
  required: boolean;
  options?: string[];
}

export type TemplateSection = {
  id: string;
  title: string;
  order: number;
  fields: TemplateField[];
}

export type CreateTemplatePayload = {
  name: string;
  description?: string;
  sections: TemplateSection[];
}

export type UpdateTemplatePayload = Partial<CreateTemplatePayload>

export async function apiListTemplates(): Promise<FormTemplate[]> {
  return apiFetch<FormTemplate[]>('/templates')
}

export async function apiCreateTemplate(payload: CreateTemplatePayload): Promise<FormTemplate> {
  return apiFetch<FormTemplate>('/templates', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiUpdateTemplate(id: string, payload: UpdateTemplatePayload): Promise<FormTemplate> {
  return apiFetch<FormTemplate>(`/templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export async function apiDeleteTemplate(id: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/templates/${id}`, { method: 'DELETE' })
}

export async function apiDuplicateTemplate(id: string): Promise<FormTemplate> {
  return apiFetch<FormTemplate>(`/templates/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

// Story 6.4 — lecture par id (brouillons IA inclus ; 404 si absent, archivé ou autre org).
export async function apiGetTemplate(id: string): Promise<FormTemplate> {
  return apiFetch<FormTemplate>(`/templates/${encodeURIComponent(id)}`)
}

// Story 6.4 — « Utiliser ce template » : brouillon IA → bibliothèque (idempotent).
export async function apiActivateTemplate(id: string): Promise<FormTemplate> {
  return apiFetch<FormTemplate>(`/templates/${encodeURIComponent(id)}/activate`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

// Campaigns

// Story 3.7 AC #6 — union exhaustive miroir de l'enum `CampaignStatus` côté
// backend (`backend/prisma/schema.prisma`). Source unique du type côté front
// pour TOUS les statuts de campagne (Campaign, SupplierDetailData, profil…).
// TypeScript fait alors office d'exhaustiveness check si un statut futur est
// ajouté côté backend (PR Prisma → erreur de compilation tant que tous les
// `switch`/comparaisons ne sont pas mis à jour côté front).
export type CampaignStatus = 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'CLOSED' | 'ARCHIVED'

export type Campaign = {
  id: string
  name: string
  description?: string | null
  slug: string
  category?: string | null
  year?: number | null
  status: CampaignStatus
  startsAt?: string | null
  dueAt?: string | null
  closedAt?: string | null
  formTemplateId?: string | null
  formTemplate?: {
    id: string
    name: string
    sections: unknown
    description?: string | null
  } | null
  createdAt: string
  updatedAt: string
  // Champs stats calculés côté backend (présents dans GET /campaigns)
  supplierCount?: number
  submittedCount?: number
  inProgressCount?: number
  notStartedCount?: number
}

export type CreateCampaignPayload = {
  name: string
  description?: string | null
  category?: string | null
  year?: number | null
  startsAt?: string | null
  dueAt?: string | null
  formTemplateId?: string | null
  // Story 3.5 — IDs catalogue à pré-assigner. Le backend crée la campagne ET
  // les CampaignSupplier dans une seule transaction (rollback complet si l'un
  // des fournisseurs échoue ; les skips déjà-assignés / introuvables ne sont
  // PAS des échecs et sont reportés dans la réponse `assignment`).
  supplierIds?: string[]
}

// Story 3.5 — résultat de l'assignation depuis le catalogue.
export type CatalogAssignmentSkipReason =
  | 'not-found'
  | 'already-assigned'
  | 'already-assigned-by-email'

export type CatalogAssignmentResult = {
  added: number
  skipped: { id: string; reason: CatalogAssignmentSkipReason }[]
  // Story 3.8 — présents uniquement quand l'ajout a eu lieu sur une campagne
  // ACTIVE (post-launch). `suppliersNotified` = nombre d'emails envoyés avec
  // succès ; `emailFailures` = ceux qui ont échoué (les CampaignSupplier sont
  // créés quand même — pas de rollback).
  suppliersNotified?: number
  emailFailures?: number
}

export type CampaignCreateResponse = Campaign & {
  assignment?: CatalogAssignmentResult
}

export async function apiListCampaigns(params?: { formTemplateId?: string; status?: string }): Promise<Campaign[]> {
  const entries = params ? Object.entries(params).filter(([, v]) => v !== undefined) : []
  const query = entries.length > 0 ? '?' + new URLSearchParams(Object.fromEntries(entries)).toString() : ''
  return apiFetch<Campaign[]>(`/campaigns${query}`)
}

export async function apiCreateCampaign(payload: CreateCampaignPayload): Promise<CampaignCreateResponse> {
  return apiFetch<CampaignCreateResponse>('/campaigns', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiGetCampaign(id: string): Promise<Campaign> {
  return apiFetch<Campaign>(`/campaigns/${id}`)
}

export async function apiUpdateCampaign(id: string, payload: Partial<CreateCampaignPayload>): Promise<Campaign> {
  return apiFetch<Campaign>(`/campaigns/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

// Campaign Suppliers

export type CampaignSupplier = {
  id: string
  name: string
  email: string
  siret?: string | null
  internalRef?: string | null
  assignedAt: string
  // Story 3.5 — FK vers le Supplier catalogue. `null` si saisie libre (avant
  // cette story, c'était systématiquement `null` côté UI). Permet d'afficher
  // le badge « Lié au catalogue » et de propager la modale complément.
  catalogSupplierId?: string | null
  // Story 3.8 — true si le fournisseur a été ajouté après le lancement
  // (campagne ACTIVE). Présent sur la réponse de `apiAddSupplier` ; absent
  // sur `apiListCampaignSuppliers` (endpoint /:id/suppliers) — n'utilisez ce
  // champ que sur les réponses dashboard/profil.
  addedPostLaunch?: boolean
  // Story 3.8 — présents uniquement quand l'ajout a eu lieu sur une campagne
  // ACTIVE (saisie libre — endpoint `POST /campaigns/:id/suppliers`).
  suppliersNotified?: number
  emailFailures?: number
}

export async function apiListCampaignSuppliers(campaignId: string): Promise<CampaignSupplier[]> {
  return apiFetch<CampaignSupplier[]>(`/campaigns/${campaignId}/suppliers`)
}

// Story 3.5 — assignation bulk depuis le catalogue
export async function apiBulkAddFromCatalog(
  campaignId: string,
  supplierIds: string[]
): Promise<CatalogAssignmentResult> {
  return apiFetch<CatalogAssignmentResult>(
    `/campaigns/${campaignId}/suppliers/bulk-from-catalog`,
    { method: 'POST', body: JSON.stringify({ supplierIds }) }
  )
}

// Story 3.5 — lier un CampaignSupplier orphelin au catalogue (Volet D)
export async function apiLinkCampaignSupplierToCatalog(
  campaignId: string,
  campaignSupplierId: string,
  supplierId: string
): Promise<{ id: string; supplierId: string }> {
  return apiFetch<{ id: string; supplierId: string }>(
    `/campaigns/${campaignId}/suppliers/${campaignSupplierId}/link-to-catalog`,
    { method: 'PATCH', body: JSON.stringify({ supplierId }) }
  )
}

export async function apiAddSupplier(
  campaignId: string,
  payload: { name: string; email: string; siret?: string; internalRef?: string }
): Promise<CampaignSupplier> {
  return apiFetch<CampaignSupplier>(`/campaigns/${campaignId}/suppliers`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export type ImportSuppliersResult = {
  added: number
  skipped: number
  invalidRows: number
  // Story 3.8 — présents uniquement sur campagne ACTIVE (import post-launch).
  suppliersNotified?: number
  emailFailures?: number
}

export async function apiImportSuppliers(
  campaignId: string,
  file: File
): Promise<ImportSuppliersResult> {
  // apiFetch force Content-Type: application/json — pour multipart on utilise fetch directement
  const { getToken } = await import('./auth')
  const token = getToken()
  const formData = new FormData()
  formData.append('file', file)
  const res = await fetch(`${API_BASE}/campaigns/${campaignId}/suppliers/import`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  })
  if (!res.ok) {
    const text = await res.text()
    let message = `API error ${res.status}: ${text}`
    try {
      const json = JSON.parse(text)
      if (typeof json.error === 'string') message = json.error
    } catch { /* not JSON */ }
    throw new Error(message)
  }
  return res.json()
}

export async function apiDeleteCampaign(id: string): Promise<void> {
  await apiFetch<void>(`/campaigns/${id}`, { method: 'DELETE' })
}

export type LaunchCampaignResult = {
  message: string
  suppliersNotified: number
  emailFailures: number
}

export async function apiLaunchCampaign(campaignId: string): Promise<LaunchCampaignResult> {
  return apiFetch<LaunchCampaignResult>(`/campaigns/${campaignId}/launch`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function apiCloseCampaign(campaignId: string): Promise<Campaign> {
  return apiFetch<Campaign>(`/campaigns/${campaignId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'CLOSED' }),
  })
}

export async function apiDuplicateCampaign(campaignId: string): Promise<Campaign> {
  return apiFetch<Campaign>(`/campaigns/${campaignId}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

// Organizations
export type OrgProfile = {
  id: string;
  name: string;
  logoUrl?: string | null;
  senderEmail?: string | null;
};

export type OrgQuota = {
  aiQuotaLimit: number;
  aiQuotaUsed: number;
  aiQuotaResetAt: string;
};

export async function apiGetOrgProfile(): Promise<OrgProfile> {
  return apiFetch<OrgProfile>(`/organizations/profile`);
}

export async function apiUpdateOrgProfile(
  payload: Partial<Pick<OrgProfile, 'name' | 'logoUrl' | 'senderEmail'>>
): Promise<OrgProfile> {
  return apiFetch<OrgProfile>(`/organizations/profile`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function apiGetOrgQuota(): Promise<OrgQuota> {
  return apiFetch<OrgQuota>('/organizations/quota');
}

// Campaign Dashboard

export type SupplierUXStatus = 'not_opened' | 'opened' | 'in_progress' | 'submitted' | 'validated'

export type DashboardSupplier = {
  id: string
  name: string
  email: string
  assignedAt: string
  deletedAt: string | null
  // Story 3.8 — true si le fournisseur a été ajouté en cours de campagne
  // (post-launch). Permet l'affichage du badge "Ajouté en cours de campagne".
  addedPostLaunch: boolean
  uxStatus: SupplierUXStatus
  lastActionAt: string | null
  remindedAt: string | null
}

export type CampaignDashboardData = {
  // Story 3.8 — métadonnées campagne exposées pour gater le bouton
  // « Ajouter un fournisseur » (visible sur DRAFT + ACTIVE).
  campaign: { id: string; name: string; status: CampaignStatus }
  suppliers: DashboardSupplier[]
  stats: {
    total: number
    submitted: number
    inProgress: number
    notStarted: number
  }
}

export async function apiGetCampaignDashboard(campaignId: string): Promise<CampaignDashboardData> {
  return apiFetch<CampaignDashboardData>(`/campaigns/${campaignId}/dashboard`)
}

// Supplier Detail

export type SupplierDetailExtraction = {
  fieldId: string
  extractedValue: string
  confidence: number
  documentUrl: string
  acceptedByUser: boolean
  rejectedByUser: boolean
}

export type SupplierDetailData = {
  supplier: {
    id: string
    name: string
    email: string
    assignedAt: string
    // Story 3.1 follow-up — FK vers le catalogue Supplier (null si CampaignSupplier saisi en
    // libre, hors catalogue). Requis pour cibler la modale `Demander un complément`.
    catalogSupplierId: string | null
    submissionStatus: SupplierSubmissionStatus
    // Story 3.8 — true si le fournisseur a été ajouté après le lancement.
    // Permet l'affichage du badge dans le header de SupplierDetailView.
    addedPostLaunch: boolean
  }
  // Story 3.3 follow-up Bug 1 — bloquer le complément si campagne fermée.
  // Story 3.7 AC #6 — typage élargi à l'union complète `CampaignStatus`
  // (5 valeurs) plutôt qu'un sous-ensemble. Le gate UI sur `ACTIVE` reste
  // explicite ; TypeScript force la prise en compte d'un futur statut.
  campaign: { status: CampaignStatus }
  survey: {
    id: string
    uxStatus: SupplierUXStatus
    openedAt: string | null
    completedAt: string | null
    validatedAt: string | null
  } | null
  template: {
    id: string
    name: string
    sections: TemplateSection[]
  }
  answers: Record<string, unknown>
  extractions: SupplierDetailExtraction[]
  stats: {
    requiredTotal: number
    requiredFilled: number
    optionalTotal: number
    optionalFilled: number
  }
}

export async function apiGetSupplierDetail(
  campaignId: string,
  supplierId: string
): Promise<SupplierDetailData> {
  return apiFetch<SupplierDetailData>(`/campaigns/${campaignId}/suppliers/${supplierId}/detail`)
}

// Communications

export type CommunicationLogEntry = {
  id: string
  type: 'invitation' | 'reminder'
  subject: string
  sentAt: string
  status: 'sent' | 'failed'
  sentByName: string | null
}

export async function apiSendReminders(
  campaignId: string,
  payload: { supplierIds: string[]; subject: string; message: string }
): Promise<{ sent: number; failed: number }> {
  return apiFetch<{ sent: number; failed: number }>(`/campaigns/${campaignId}/remind`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiGetSupplierCommunications(
  campaignId: string,
  supplierId: string
): Promise<CommunicationLogEntry[]> {
  return apiFetch<CommunicationLogEntry[]>(
    `/campaigns/${campaignId}/suppliers/${supplierId}/communications`
  )
}

// Batch Extraction Review

export type BatchExtractionItem = {
  id: string
  surveyId: string
  fieldId: string
  fieldLabel: string
  extractedValue: string
  confidence: number
  documentUrl: string
  supplierId: string | null
  supplierName: string
  supplierEmail: string
}

export async function apiGetCampaignExtractions(
  campaignId: string,
  confidenceLt: number = 0.85
): Promise<BatchExtractionItem[]> {
  return apiFetch<BatchExtractionItem[]>(
    `/campaigns/${campaignId}/extractions?confidence_lt=${confidenceLt}`
  )
}

export async function apiPatchExtraction(
  extractionId: string,
  payload: { action: 'accept' | 'reject'; note?: string }
): Promise<{ id: string; acceptedByUser: boolean; rejectedByUser: boolean; reviewNote: string | null }> {
  return apiFetch(`/extractions/${extractionId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

// Export CSV Campagne (Story 7.1)

export async function apiDownloadCampaignCsv(
  campaignId: string
): Promise<{ blob: Blob; filename: string; isEmpty: boolean }> {
  const res = await apiFetchRaw(`/campaigns/${campaignId}/export?format=csv`)
  const exportCount = res.headers.get('X-Export-Count')
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/)
  const filename = filenameMatch?.[1] ?? `export-campagne-${campaignId}.csv`
  const blob = await res.blob()
  return {
    blob,
    filename,
    isEmpty: exportCount === '0',
  }
}

// Export Excel Campagne (Story 7.2)

export async function apiDownloadCampaignExcel(
  campaignId: string
): Promise<{ blob: Blob; filename: string; isEmpty: boolean }> {
  const res = await apiFetchRaw(`/campaigns/${campaignId}/export?format=excel`)
  const exportCount = res.headers.get('X-Export-Count')
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/)
  const filename = filenameMatch?.[1] ?? `export-campagne-${campaignId}.xlsx`
  const blob = await res.blob()
  return {
    blob,
    filename,
    isEmpty: exportCount === '0',
  }
}

// Dashboard stats
export type CampaignProgress = {
  id: string
  name: string
  total: number
  submitted: number
  progress: number
}

export type MonthlyDataEntry = { month: string } & Record<string, string | number>

export type DashboardStats = {
  activeCampaignCount: number
  supplierCount: number
  responseRate: number | null
  monthlyData: MonthlyDataEntry[]
  campaignProgress: CampaignProgress[]
}

export async function apiGetDashboardStats(): Promise<DashboardStats> {
  return apiFetch<DashboardStats>('/dashboard/stats')
}

// Suppression RGPD données fournisseur (Story 7.3)
export async function apiDeleteSupplierData(campaignId: string, supplierId: string): Promise<void> {
  await apiFetch(`/campaigns/${campaignId}/suppliers/${supplierId}/data`, { method: 'DELETE' })
}

// ─── Suppliers (org-level, Story 2.2) ────────────────────────────────────────

export type SupplierSubmissionStatus =
  | 'NOT_SENT'
  | 'SENT'
  | 'OPENED'
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'NEEDS_COMPLEMENT'
  | 'COMPLEMENT_SENT'
  | 'RESUBMITTED'
  | 'VALIDATED'

export type SupplierAggregateStatus = SupplierSubmissionStatus | 'NO_CAMPAIGN'

export type SupplierListFilter = 'all' | 'pending' | 'received' | 'validated' | 'overdue'

export type SupplierListItem = {
  id: string
  organizationId: string
  name: string
  email: string
  company: string | null
  phone: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
  aggregateStatus: SupplierAggregateStatus
  isOverdue: boolean
  campaignCount: number
  lastActivityAt: string | null
  // Story 3.5 intent gap — visibilité cross-campagne dans le picker. Compte
  // les Campaign DRAFT+ACTIVE hors `excludeCampaignId` ; preview = max 3 noms.
  activeCampaignCount: number
  activeCampaigns: { id: string; name: string }[]
}

export type SupplierCounters = {
  all: number
  pending: number
  received: number
  validated: number
  overdue: number
}

export type SupplierListResponse = {
  data: SupplierListItem[]
  total: number
  page: number
  pageSize: number
  counters: SupplierCounters
}

export type SupplierSortBy = 'name' | 'createdAt' | 'updatedAt' | 'status' | 'lastActivity'
export type SupplierSortOrder = 'asc' | 'desc'

export type ListSuppliersParams = {
  page?: number
  pageSize?: number
  search?: string
  status?: SupplierListFilter
  sortBy?: SupplierSortBy
  sortOrder?: SupplierSortOrder
  // Story 3.5 — exclut les fournisseurs déjà assignés à cette campagne
  // (CampaignSupplier non soft-deleted). Utilisé par le picker catalogue.
  excludeCampaignId?: string
}

export async function apiListSuppliers(
  params: ListSuppliersParams = {}
): Promise<SupplierListResponse> {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== ''
  ) as [string, string | number][]
  const query = entries.length > 0
    ? '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()
    : ''
  return apiFetch<SupplierListResponse>(`/suppliers${query}`)
}

export type CreateSupplierInput = {
  name: string
  email: string
  company?: string | null
  phone?: string | null
  notes?: string | null
}

export type OrgSupplier = {
  id: string
  organizationId: string
  name: string
  email: string
  company: string | null
  phone: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
}

export async function apiCreateSupplier(payload: CreateSupplierInput): Promise<OrgSupplier> {
  return apiFetch<OrgSupplier>('/suppliers', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Story 3.5 — lookup catalogue par email exact (lower-case, trim) pour le
// bandeau de dédoublonnage de CampaignSupplierDialog.
export type SearchCatalogByEmailResponse = {
  match: { id: string; name: string; email: string } | null
}

export async function apiSearchCatalogByEmail(email: string): Promise<SearchCatalogByEmailResponse> {
  const q = new URLSearchParams({ email })
  return apiFetch<SearchCatalogByEmailResponse>(`/suppliers/search-by-email?${q.toString()}`)
}

// Sanitize un filename serveur avant de le passer à `a.download` — retire
// path separators et préfixes `..`, force l'extension `.csv`. Protège contre
// un backend compromis qui renverrait `../../secret.txt`.
function sanitizeCsvFilename(raw: string | undefined, fallback: string): string {
  if (!raw) return fallback
  const basename = raw.replace(/[/\\]/g, '').replace(/^\.+/, '').trim()
  if (!basename || !/\.csv$/i.test(basename)) return fallback
  return basename
}

// Export bulk CSV (Story 2.4) — POST avec body JSON pour accepter jusqu'à 500 IDs
// sans buter sur une URL-length limit. `apiFetchRaw` ne supporte que GET ; on
// inline `fetch` ici (pattern isolé).
export async function apiDownloadSuppliersCsv(
  ids: string[]
): Promise<{ blob: Blob; filename: string; exportCount: number }> {
  const token = getToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(buildUrl('/suppliers/export'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ ids }),
  })
  if (!res.ok) throw await buildApiError(res)
  const rawCount = Number(res.headers.get('X-Export-Count') ?? '0')
  const exportCount = Number.isFinite(rawCount) ? Math.max(0, Math.floor(rawCount)) : 0
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/)
  const fallback = `export-fournisseurs-${new Date().toISOString().slice(0, 10)}.csv`
  const filename = sanitizeCsvFilename(filenameMatch?.[1], fallback)
  const blob = await res.blob()
  return { blob, filename, exportCount }
}

// ─── Suppliers — Profil cross-campagne (Story 2.3) ───────────────────────────

export type SupplierProfileCampaign = {
  campaignSupplierId: string
  campaignId: string
  campaignName: string
  campaignStatus: CampaignStatus
  dueAt: string | null
  assignedAt: string
  submissionStatus: SupplierSubmissionStatus
  submittedAt: string | null
  isOverdue: boolean
  requiredFilled: number
  requiredTotal: number
  lastActivityAt: string
  // Story 3.8 — true si le fournisseur a été ajouté en cours de campagne
  // (post-launch). Permet l'affichage du badge sur la vue détail campagne.
  addedPostLaunch: boolean
  // Story 3.1 — sections brutes du template (peut être `null` si la campagne
  // n'a pas de template). Utilisé par la modale « Demander un complément »
  // pour lister les champs ciblables sans fetch supplémentaire.
  formTemplateSections: TemplateSection[] | null
}

// Le header du profil ne contient pas `lastActivityAt` (présent seulement dans
// la liste, où il est calculé côté serveur comme max(CampaignSupplier.updatedAt)),
// ni `activeCampaignCount`/`activeCampaigns` (spécifiques au picker catalogue
// Story 3.5). On réutilise la shape liste pour tout le reste afin d'éviter la
// divergence.
export type SupplierProfileHeader = Omit<
  SupplierListItem,
  'lastActivityAt' | 'activeCampaignCount' | 'activeCampaigns'
>

export type SupplierProfile = {
  supplier: SupplierProfileHeader
  campaigns: SupplierProfileCampaign[]
}

export async function apiGetSupplierProfile(id: string): Promise<SupplierProfile> {
  return apiFetch<SupplierProfile>(`/suppliers/${encodeURIComponent(id)}/profile`)
}

// ─── Suppliers — Demande de complément ciblé (Story 3.1) ─────────────────────

export type RequestComplementBody = {
  campaignSupplierId: string
  fieldIds: string[]
  message?: string
}

export type ComplementRequestResponse = {
  complementRequest: {
    id: string
    campaignSupplierId: string
    requestedFieldIds: string[]
    message: string | null
    requestedById: string | null
    createdAt: string
  }
  supplier: {
    id: string
    // `null` si le recalcul best-effort post-transaction a échoué côté backend —
    // la demande EST persistée, l'agrégat sera resynchronisé au prochain load().
    aggregateStatus: SupplierAggregateStatus | null
  }
  // Story 3.2 — statut de l'envoi email. `emailDelivered: true` ⇒ transition
  // vers COMPLEMENT_SENT faite côté backend et `complementSentAt` renseigné.
  emailDelivered: boolean
  emailError?: string
  complementSentAt: string | null
  communicationLogId: string | null
}

export async function apiRequestComplementFields(
  supplierId: string,
  body: RequestComplementBody
): Promise<ComplementRequestResponse> {
  return apiFetch<ComplementRequestResponse>(
    `/suppliers/${encodeURIComponent(supplierId)}/complement-request`,
    { method: 'POST', body: JSON.stringify(body) }
  )
}

// ─── Suppliers — Relance bulk cross-campagne (Story 3.2) ─────────────────────

export type BulkRemindBody = {
  supplierIds: string[]
  subject: string
  message: string
}

export type BulkRemindDetail =
  | {
      supplierId: string
      supplierName: string
      campaignId: string
      campaignName: string
      status: 'sent'
    }
  | {
      supplierId: string
      supplierName: string
      campaignId: string
      campaignName: string
      status: 'failed'
      error: string
    }

export type BulkRemindResponse = {
  sent: number
  failed: number
  skipped: number
  details: BulkRemindDetail[]
  // Patch P-4 (code review Story 3.2) : true si le bulk-write CommunicationLog
  // a échoué. Les emails sont partis mais l'audit n'est pas persisté — Marie
  // doit en être avertie (toast info) pour conformité RGPD.
  loggingFailed: boolean
}

export async function apiBulkRemindSuppliers(
  body: BulkRemindBody
): Promise<BulkRemindResponse> {
  return apiFetch<BulkRemindResponse>('/suppliers/bulk-remind', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

// ─── Story 5.5 — Chat agents (/api/chat) ─────────────────────────────────────
export async function apiListConversations(): Promise<ChatConversationSummary[]> {
  return apiFetch<ChatConversationSummary[]>('/chat')
}

export async function apiCreateConversation(title?: string): Promise<ChatConversationCreated> {
  return apiFetch<ChatConversationCreated>('/chat', {
    method: 'POST',
    body: JSON.stringify(title ? { title } : {}),
  })
}

export async function apiGetConversationMessages(id: string): Promise<PersistedChatMessage[]> {
  return apiFetch<PersistedChatMessage[]>(`/chat/${encodeURIComponent(id)}`)
}

// Story 7.1 — confirmation / annulation d'une action Tier 2. Les erreurs HTTP
// (404 / 409 / 410 / 429) remontent en `ApiError` (`status` exploitable).
export async function apiConfirmChatAction(
  conversationId: string,
  actionId: string
): Promise<ChatActionConfirmResponse> {
  return apiFetch<ChatActionConfirmResponse>(
    `/chat/${encodeURIComponent(conversationId)}/actions/${encodeURIComponent(actionId)}/confirm`,
    { method: 'POST' }
  )
}

export async function apiCancelChatAction(
  conversationId: string,
  actionId: string
): Promise<{ state: 'cancelled' }> {
  return apiFetch<{ state: 'cancelled' }>(
    `/chat/${encodeURIComponent(conversationId)}/actions/${encodeURIComponent(actionId)}/cancel`,
    { method: 'POST' }
  )
}

// Seul usage autorisé de `fetch` pour le streaming : ajoute Bearer + base URL,
// lève `ApiError` (message FR du backend) si `!res.ok`, et renvoie la Response
// BRUTE sans lire le corps (le lecteur SSE la consomme).
export async function apiFetchStream(path: string, init?: RequestInit): Promise<Response> {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    ...(init?.headers as Record<string, string> | undefined),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(buildUrl(path), { ...init, headers })
  if (!res.ok) throw await buildApiError(res)
  return res
}
